import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Flame,
  RefreshCw,
  Archive,
  ArchiveRestore,
  CheckCircle2,
  ChefHat,
  Bell,
  Clock,
  CreditCard,
  Wallet,
  Phone,
  User,
  MessageSquare,
  MapPin,
  Loader2,
  Lock,
  Unlock,
  Tag,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_FLOW = [
  { key: "new", label: "New", next: "in_progress", nextLabel: "Start Cooking", icon: Bell, color: "#FF3B30" },
  { key: "in_progress", label: "In Progress", next: "ready", nextLabel: "Mark Ready", icon: ChefHat, color: "#FF8C00" },
  { key: "ready", label: "Ready", next: "completed", nextLabel: "Hand Off", icon: CheckCircle2, color: "#22c55e" },
  { key: "completed", label: "Completed", next: null, nextLabel: null, icon: CheckCircle2, color: "#a1a1aa" },
];

const TAB_OPTIONS = [
  { key: "active", label: "Active" },
  { key: "archived", label: "Archived" },
];

const MENU_ITEMS_REF = [
  { id: "flapjack_chicken_bombs", name: "Flapjack Chicken Bombs (2)", price: "$10" },
  { id: "fried_fish_plate", name: "Fried Fish Plate", price: "$10" },
  { id: "fried_af_chicken_fingers", name: "Fried AF Chicken Fingers", price: "$12" },
  { id: "betty_cracker_chicken_bowl", name: "Betty Cracker Chicken Mashed Potato Bowl", price: "$14" },
  { id: "fried_oreos", name: "Forbidden AF Fried Oreos (3)", price: "$6" },
  { id: "tavern_cheese_pizza", name: "Tavern Cheese Pizza", price: "$16" },
  { id: "tavern_pepperoni_pizza", name: "Tavern Pepperoni Pizza", price: "$16" },
  { id: "hot_honey_jalapeno_pizza", name: "Smokey AF Hot Honey Jalapeño Pizza", price: "$16" },
  { id: "german_bratwurst", name: "Grilled German Bratwurst", price: "$7" },
  { id: "dessert_pizza", name: "Streusel Dessert Pizza", price: "$14" },
  { id: "bbq_pizza", name: "BBQ Pizza", price: "$16" },
  { id: "bbq_boat", name: "BBQ Boat", price: "$14" },
  { id: "bbq_bavarian_pretzel", name: "Bavarian Behemoth Pretzel", price: "$12" },
  { id: "pulled_pork_sandwich", name: "Pulled Pork Sandwich Meal", price: "$14" },
  { id: "japanese_bbq_burger", name: "Japanese BBQ Burger", price: "$14" },
  { id: "loaded_pork_fries", name: "Loaded Pork Fries", price: "$14" },
  { id: "pork_crack_mash_bowl", name: "Pork Crack Mash Bowl", price: "$14" },
  { id: "mini_pulled_pork_sandwich", name: "Mini Pulled Pork Sandwich", price: "$7" },
  { id: "mini_bbq_boat", name: "Mini BBQ Boat", price: "$7" },
  { id: "nacho_pork_sandwich", name: "Nacho Jalapeño Pulled Pork Sandwich", price: "$10" },
  { id: "japanese_pork_sandwich", name: "Japanese Pulled Pork Sandwich", price: "$10" },
  { id: "nacho_pork_pizza", name: "Smokey AF Nacho Jalapeño Pork Pizza", price: "$16" },
  { id: "bbq_sushi", name: "BBQ Sushi", price: "$10" },
  { id: "jerk_chicken_sushi", name: "Rastafarian Jerk Chicken Sushi Roll", price: "$10" },
  { id: "pepperoni_pizza", name: "Pepperoni Pizza", price: "$16" },
  { id: "chicken_alfredo_pizza", name: "Chicken Alfredo Pizza", price: "$16" },
  { id: "garlic_parmesan_wings", name: "Garlic Parmesan Wings (6)", price: "$12" },
  { id: "bbq_wings", name: "BBQ Wings (6)", price: "$12" },
  { id: "buffalo_wings", name: "Buffalo Wings (6)", price: "$12" },
  { id: "chipotle_firecracker_wings", name: "Garlic Parm Chipotle Firecracker Wings (6)", price: "$12" },
  { id: "chicken_bacon_ranch_pizza", name: "Chicken Bacon Ranch Pizza", price: "$16" },
  { id: "bbq_chicken_sandwich", name: "BBQ Chicken Sandwich", price: "$10" },
  { id: "order_of_fries", name: "Order of Fries", price: "$5" },
];

const SPECIALS_REF = [
  {
    id: "pulled_pork_special",
    label: "The Midnight Special · $5 Pulled Pork Sandwich",
    desc: "Just the sandwich — no fries. Customers see it at $5 while this is on.",
    onLabel: "Turn On Midnight Special",
  },
  {
    id: "student_loan_special",
    label: "The Student Loan Special · $5 Single BBQ Boat",
    desc: "One BBQ Boat — college night deal. Customers see it at $5 while this is on.",
    onLabel: "Turn On Student Loan Special",
  },
];

function timeAgo(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function fmtMoney(cents) {
  return `$${((cents || 0) / 100).toFixed(2)}`;
}

function OrderCard({ order, onChanged }) {
  const [busy, setBusy] = useState(null);
  const flowStep = STATUS_FLOW.find((s) => s.key === (order.order_status || "new")) || STATUS_FLOW[0];
  const Icon = flowStep.icon;

  const callPatch = async (body, label) => {
    setBusy(label);
    try {
      await axios.patch(`${API}/admin/orders/${order.order_id}`, body);
      onChanged();
    } catch (e) {
      alert("Could not update order: " + (e?.response?.data?.detail || e.message));
    } finally {
      setBusy(null);
    }
  };

  const advanceStatus = () =>
    flowStep.next && callPatch({ order_status: flowStep.next }, "advance");
  const archive = () => callPatch({ archived: true, order_status: "completed" }, "archive");
  const restore = () => callPatch({ archived: false }, "restore");

  const isCash = order.payment_method === "cash";
  const cashPending = isCash && order.payment_status === "pending_cash";
  const paid = order.payment_status === "paid";

  return (
    <article
      data-testid={`order-card-${order.order_id}`}
      className="bg-[#111] border border-white/5 hover:border-white/15 transition-colors flex flex-col"
    >
      <header className="flex items-start justify-between gap-3 px-5 py-4 border-b border-white/5">
        <div>
          <div className="flex items-center gap-2">
            <Icon className="w-4 h-4" style={{ color: flowStep.color }} />
            <span
              className="font-['Outfit'] text-[10px] uppercase tracking-[0.3em]"
              style={{ color: flowStep.color }}
            >
              {flowStep.label}
            </span>
          </div>
          <h3 className="font-['Anton'] text-2xl uppercase tracking-wider text-white mt-1">
            #{order.order_number || order.order_id.slice(0, 8)}
          </h3>
          {order.table_number && (
            <div
              data-testid={`order-table-${order.order_id}`}
              className="mt-1 inline-flex items-center gap-1.5 font-['Anton'] uppercase tracking-widest text-[#FF8C00] text-sm"
            >
              <MapPin className="w-3.5 h-3.5" />
              {/^\d+$/.test(order.table_number)
                ? `Table ${order.table_number}`
                : order.table_number}
            </div>
          )}
        </div>
        <div className="text-right">
          <div className="font-['Anton'] text-2xl text-white">
            {fmtMoney(order.amount_total_cents || order.amount_cents)}
          </div>
          <div className="flex items-center gap-1 justify-end mt-1 font-['Outfit'] text-[10px] uppercase tracking-widest text-zinc-500">
            <Clock className="w-3 h-3" /> {timeAgo(order.created_at)}
          </div>
        </div>
      </header>

      <div className="px-5 pt-3 flex flex-wrap gap-2">
        {isCash ? (
          <span
            className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest font-['Anton'] border ${
              cashPending
                ? "border-[#FF8C00] text-[#FF8C00]"
                : "border-emerald-500 text-emerald-400"
            }`}
          >
            <Wallet className="w-3 h-3" />
            {cashPending ? "Cash · Unpaid" : "Cash · Paid"}
          </span>
        ) : (
          <span
            className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest font-['Anton'] border ${
              paid
                ? "border-emerald-500 text-emerald-400"
                : "border-zinc-600 text-zinc-400"
            }`}
          >
            <CreditCard className="w-3 h-3" />
            Card · {order.payment_status || "pending"}
          </span>
        )}
      </div>

      {(order.customer_name || order.customer_phone) && (
        <div className="px-5 pt-3 flex flex-wrap gap-x-4 gap-y-1 font-['Outfit'] text-sm text-zinc-300">
          {order.customer_name && (
            <span className="inline-flex items-center gap-1.5">
              <User className="w-3.5 h-3.5 text-zinc-500" />
              {order.customer_name}
            </span>
          )}
          {order.customer_phone && (
            <a
              href={`tel:${order.customer_phone}`}
              className="inline-flex items-center gap-1.5 hover:text-white"
            >
              <Phone className="w-3.5 h-3.5 text-zinc-500" />
              {order.customer_phone}
            </a>
          )}
        </div>
      )}

      <ul className="px-5 py-4 space-y-1 font-['Outfit'] text-sm text-zinc-300 flex-1">
        {(order.cart || []).map((line, i) => (
          <li key={i} className="flex justify-between gap-3">
            <span>
              <span className="text-[#FF8C00] font-semibold">
                {line.quantity}×
              </span>{" "}
              {line.name}
            </span>
            <span className="text-zinc-500">{fmtMoney(line.line_total_cents)}</span>
          </li>
        ))}
      </ul>

      {order.customer_notes && (
        <div className="px-5 pb-4">
          <div className="bg-[#FF3B30]/10 border-l-2 border-[#FF3B30] px-3 py-2">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.3em] font-['Anton'] text-[#FF3B30]">
              <MessageSquare className="w-3 h-3" />
              Notes
            </div>
            <p className="mt-1 font-['Outfit'] text-sm text-zinc-200 italic">
              {order.customer_notes}
            </p>
          </div>
        </div>
      )}

      <footer className="px-5 py-4 border-t border-white/5 flex flex-wrap gap-2">
        {order.archived ? (
          <button
            onClick={restore}
            disabled={!!busy}
            data-testid={`order-restore-${order.order_id}`}
            className="flex-1 inline-flex items-center justify-center gap-2 border border-white/30 hover:border-white text-white font-['Anton'] tracking-widest uppercase text-xs py-2.5 transition-colors disabled:opacity-50"
          >
            {busy === "restore" ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <ArchiveRestore className="w-3.5 h-3.5" />
            )}
            Restore
          </button>
        ) : (
          <>
            {flowStep.next && (
              <button
                onClick={advanceStatus}
                disabled={!!busy}
                data-testid={`order-advance-${order.order_id}`}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-white text-black hover:bg-[#FF3B30] hover:text-white font-['Anton'] tracking-widest uppercase text-xs py-2.5 transition-colors disabled:opacity-50"
              >
                {busy === "advance" ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : null}
                {flowStep.nextLabel}
              </button>
            )}
            <button
              onClick={archive}
              disabled={!!busy}
              data-testid={`order-archive-${order.order_id}`}
              className="inline-flex items-center justify-center gap-2 border border-white/20 hover:border-[#FF3B30] hover:text-[#FF3B30] text-zinc-400 font-['Anton'] tracking-widest uppercase text-xs py-2.5 px-4 transition-colors disabled:opacity-50"
            >
              {busy === "archive" ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Archive className="w-3.5 h-3.5" />
              )}
              Done
            </button>
          </>
        )}
      </footer>
    </article>
  );
}

export default function Admin() {
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("active");
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ by_status: {}, archived: 0 });
  const [kitchen, setKitchen] = useState({ is_open: true, message: null });
  const [kitchenBusy, setKitchenBusy] = useState(false);
  const [availability, setAvailability] = useState({});
  const [itemBusy, setItemBusy] = useState(null);
  const [promos, setPromos] = useState({});

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [ordersRes, statsRes, kitchenRes, availRes, promosRes] = await Promise.all([
        axios.get(`${API}/admin/orders`, { params: { view: tab } }),
        axios.get(`${API}/admin/orders/stats`),
        axios.get(`${API}/kitchen/status`),
        axios.get(`${API}/menu/availability`),
        axios.get(`${API}/promos`),
      ]);
      setOrders(ordersRes.data.orders || []);
      setStats(statsRes.data || { by_status: {}, archived: 0 });
      setKitchen(kitchenRes.data || { is_open: true, message: null });
      setAvailability(availRes.data || {});
      setPromos(promosRes.data || {});
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  }, [tab]);

  const toggleKitchen = async () => {
    setKitchenBusy(true);
    try {
      const newOpen = !kitchen.is_open;
      const { data } = await axios.post(`${API}/admin/kitchen/status`, {
        is_open: newOpen,
        message: newOpen ? null : "Closed for the night. Back tomorrow.",
      });
      setKitchen(data);
    } catch (e) {
      alert("Could not update kitchen status: " + (e?.response?.data?.detail || e.message));
    } finally {
      setKitchenBusy(false);
    }
  };

  const toggleItem = async (itemId) => {
    setItemBusy(itemId);
    try {
      const newAvail = availability[itemId] === false;
      const { data } = await axios.post(
        `${API}/admin/menu/availability/${itemId}`,
        { is_available: newAvail }
      );
      setAvailability((prev) => ({ ...prev, [itemId]: data.is_available }));
    } catch (e) {
      alert("Could not update item: " + (e?.response?.data?.detail || e.message));
    } finally {
      setItemBusy(null);
    }
  };

  const toggleWingSpecial = async () => {
    setItemBusy("wing_special");
    try {
      const { data } = await axios.post(`${API}/admin/promos/wing_special`, {
        active: promos.wing_special !== true,
      });
      setPromos(data);
    } catch (e) {
      alert("Could not update wing special: " + (e?.response?.data?.detail || e.message));
    } finally {
      setItemBusy(null);
    }
  };

  const toggleSundayMenu = async () => {
    setItemBusy("sunday_menu");
    try {
      const { data } = await axios.post(`${API}/admin/promos/sunday_menu`, {
        active: promos.sunday_menu !== true,
      });
      setPromos((p) => ({ ...p, ...data }));
    } catch (e) {
      alert("Could not update Sunday menu: " + (e?.response?.data?.detail || e.message));
    } finally {
      setItemBusy(null);
    }
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 12000);
    return () => clearInterval(t);
  }, [refresh]);

  const grouped = useMemo(() => {
    if (tab !== "active") return null;
    const buckets = { new: [], in_progress: [], ready: [], completed: [] };
    orders.forEach((o) => {
      const k = o.order_status || "new";
      (buckets[k] || buckets.new).push(o);
    });
    return buckets;
  }, [orders, tab]);

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-white/10 bg-black/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Flame className="w-5 h-5 text-[#FF3B30]" />
            <span className="font-['Anton'] text-xl tracking-widest uppercase">
              Smokey <span className="text-[#FF3B30]">AF</span> · Pitmaster
            </span>
          </div>
          <button
            onClick={refresh}
            data-testid="admin-refresh"
            className="inline-flex items-center gap-2 border border-white/15 hover:border-white text-xs font-['Anton'] tracking-widest uppercase px-3 py-2 transition-colors"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        {/* Kitchen open/closed control */}
        <div
          data-testid="kitchen-control"
          className={`mb-8 border-2 px-5 md:px-6 py-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4 ${
            kitchen.is_open
              ? "border-emerald-500/40 bg-emerald-500/5"
              : "border-[#FF3B30] bg-[#FF3B30]/10"
          }`}
        >
          <div className="flex items-center gap-4">
            {kitchen.is_open ? (
              <Unlock className="w-6 h-6 text-emerald-400" />
            ) : (
              <Lock className="w-6 h-6 text-[#FF3B30]" />
            )}
            <div>
              <div
                className={`font-['Outfit'] text-[10px] uppercase tracking-[0.3em] ${
                  kitchen.is_open ? "text-emerald-400" : "text-[#FF3B30]"
                }`}
              >
                Kitchen Status
              </div>
              <h2 className="font-['Anton'] text-2xl md:text-3xl uppercase tracking-tight text-white leading-none mt-1">
                {kitchen.is_open ? "Open · Taking Orders" : "Closed · No New Orders"}
              </h2>
              {!kitchen.is_open && kitchen.message && (
                <p className="font-['Outfit'] text-xs text-zinc-400 mt-1">
                  Customers see: "{kitchen.message}"
                </p>
              )}
            </div>
          </div>
          <button
            onClick={toggleKitchen}
            disabled={kitchenBusy}
            data-testid="kitchen-toggle"
            className={`inline-flex items-center justify-center gap-2 font-['Anton'] tracking-widest uppercase text-sm px-6 py-3 transition-colors disabled:opacity-50 ${
              kitchen.is_open
                ? "bg-[#FF3B30] hover:bg-white hover:text-black text-white"
                : "bg-emerald-500 hover:bg-white hover:text-black text-white"
            }`}
          >
            {kitchenBusy ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : kitchen.is_open ? (
              <Lock className="w-4 h-4" />
            ) : (
              <Unlock className="w-4 h-4" />
            )}
            {kitchen.is_open ? "Shut Down Orders" : "Open Kitchen"}
          </button>
        </div>

        {/* Sunday Menu toggle */}
        {(() => {
          const sundayOn = promos.sunday_menu === true;
          const sundayBusy = itemBusy === "sunday_menu";
          return (
            <div
              data-testid="special-control-sunday_menu"
              className={`mb-8 border-2 px-5 md:px-6 py-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4 ${
                sundayOn
                  ? "border-[#4A90D9] bg-[#4A90D9]/10"
                  : "border-white/15 bg-white/[0.02]"
              }`}
            >
              <div className="flex items-center gap-4">
                <Tag
                  className={`w-6 h-6 ${sundayOn ? "text-[#4A90D9]" : "text-zinc-500"}`}
                />
                <div>
                  <div
                    className={`font-['Outfit'] text-[10px] uppercase tracking-[0.3em] ${
                      sundayOn ? "text-[#4A90D9]" : "text-zinc-500"
                    }`}
                  >
                    Sunday Menu · Limited Items Only
                  </div>
                  <h2 className="font-['Anton'] text-2xl md:text-3xl uppercase tracking-tight text-white leading-none mt-1">
                    {sundayOn ? "Sunday Menu is On" : "Full Menu Showing"}
                  </h2>
                  <p className="font-['Outfit'] text-xs text-zinc-400 mt-1">
                    Customers only see: both bowls, pulled pork meal, loaded fries,
                    pretzel, 3 wings, mini pulled pork, fries + Oreos. Everything
                    else is hidden — no sold-out tags. Specials & 86 buttons still work.
                  </p>
                </div>
              </div>
              <button
                onClick={toggleSundayMenu}
                disabled={sundayBusy}
                data-testid="special-toggle-sunday_menu"
                className={`inline-flex items-center justify-center gap-2 font-['Anton'] tracking-widest uppercase text-sm px-6 py-3 transition-colors disabled:opacity-50 ${
                  sundayOn
                    ? "bg-[#FF3B30] hover:bg-white hover:text-black text-white"
                    : "bg-[#4A90D9] hover:bg-white hover:text-black text-black"
                }`}
              >
                {sundayBusy ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Tag className="w-4 h-4" />
                )}
                {sundayOn ? "Back to Full Menu" : "Turn On Sunday Menu"}
              </button>
            </div>
          );
        })()}

        {/* Specials toggles */}
        {SPECIALS_REF.map((sp) => {
          const specialOn = availability[sp.id] === true;
          const specialBusy = itemBusy === sp.id;
          return (
            <div
              key={sp.id}
              data-testid={`special-control-${sp.id}`}
              className={`mb-8 border-2 px-5 md:px-6 py-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4 ${
                specialOn
                  ? "border-[#FF8C00] bg-[#FF8C00]/10"
                  : "border-white/15 bg-white/[0.02]"
              }`}
            >
              <div className="flex items-center gap-4">
                <Tag
                  className={`w-6 h-6 ${
                    specialOn ? "text-[#FF8C00]" : "text-zinc-500"
                  }`}
                />
                <div>
                  <div
                    className={`font-['Outfit'] text-[10px] uppercase tracking-[0.3em] ${
                      specialOn ? "text-[#FF8C00]" : "text-zinc-500"
                    }`}
                  >
                    {sp.label}
                  </div>
                  <h2 className="font-['Anton'] text-2xl md:text-3xl uppercase tracking-tight text-white leading-none mt-1">
                    {specialOn ? "Live on the Menu" : "Off the Menu"}
                  </h2>
                  <p className="font-['Outfit'] text-xs text-zinc-400 mt-1">
                    {sp.desc}
                  </p>
                </div>
              </div>
              <button
                onClick={() => toggleItem(sp.id)}
                disabled={specialBusy}
                data-testid={`special-toggle-${sp.id}`}
                className={`inline-flex items-center justify-center gap-2 font-['Anton'] tracking-widest uppercase text-sm px-6 py-3 transition-colors disabled:opacity-50 ${
                  specialOn
                    ? "bg-[#FF3B30] hover:bg-white hover:text-black text-white"
                    : "bg-[#FF8C00] hover:bg-white hover:text-black text-black"
                }`}
              >
                {specialBusy ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Tag className="w-4 h-4" />
                )}
                {specialOn ? "End Special" : sp.onLabel}
              </button>
            </div>
          );
        })}

        {/* Wednesday Wing Special toggle */}
        {(() => {
          const wingOn = promos.wing_special === true;
          const wingBusy = itemBusy === "wing_special";
          return (
            <div
              data-testid="special-control-wing_special"
              className={`mb-8 border-2 px-5 md:px-6 py-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4 ${
                wingOn
                  ? "border-[#FF8C00] bg-[#FF8C00]/10"
                  : "border-white/15 bg-white/[0.02]"
              }`}
            >
              <div className="flex items-center gap-4">
                <Tag
                  className={`w-6 h-6 ${wingOn ? "text-[#FF8C00]" : "text-zinc-500"}`}
                />
                <div>
                  <div
                    className={`font-['Outfit'] text-[10px] uppercase tracking-[0.3em] ${
                      wingOn ? "text-[#FF8C00]" : "text-zinc-500"
                    }`}
                  >
                    Wednesday Wing Special · All Wings $9
                  </div>
                  <h2 className="font-['Anton'] text-2xl md:text-3xl uppercase tracking-tight text-white leading-none mt-1">
                    {wingOn ? "Live on the Menu" : "Off the Menu"}
                  </h2>
                  <p className="font-['Outfit'] text-xs text-zinc-400 mt-1">
                    Drops all 4 wing flavors from $12 to $9 while this is on.
                  </p>
                </div>
              </div>
              <button
                onClick={toggleWingSpecial}
                disabled={wingBusy}
                data-testid="special-toggle-wing_special"
                className={`inline-flex items-center justify-center gap-2 font-['Anton'] tracking-widest uppercase text-sm px-6 py-3 transition-colors disabled:opacity-50 ${
                  wingOn
                    ? "bg-[#FF3B30] hover:bg-white hover:text-black text-white"
                    : "bg-[#FF8C00] hover:bg-white hover:text-black text-black"
                }`}
              >
                {wingBusy ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Tag className="w-4 h-4" />
                )}
                {wingOn ? "End Wing Special" : "Turn On $9 Wings"}
              </button>
            </div>
          );
        })()}

        {/* Per-item availability */}
        <div
          data-testid="item-availability-panel"
          className="mb-10 bg-[#0f0f0f] border border-white/10 p-5 md:p-6"
        >
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-[#FF8C00]" />
            <h3 className="font-['Outfit'] text-xs uppercase tracking-[0.4em] text-[#FF8C00]">
              Menu Items · 86 Toggle
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {MENU_ITEMS_REF.map((item) => {
              const available = availability[item.id] !== false;
              const busy = itemBusy === item.id;
              return (
                <div
                  key={item.id}
                  data-testid={`item-row-${item.id}`}
                  className={`flex items-center justify-between gap-4 border px-4 py-3 transition-colors ${
                    available
                      ? "border-emerald-500/30 bg-emerald-500/5"
                      : "border-[#FF3B30]/40 bg-[#FF3B30]/5"
                  }`}
                >
                  <div className="min-w-0">
                    <div className="font-['Anton'] uppercase tracking-tight text-white text-base leading-tight truncate">
                      {item.name}
                    </div>
                    <div
                      className={`font-['Outfit'] text-[10px] uppercase tracking-[0.3em] mt-0.5 ${
                        available ? "text-emerald-400" : "text-[#FF3B30]"
                      }`}
                    >
                      {available ? "Available" : "Sold Out"}
                    </div>
                  </div>
                  <button
                    onClick={() => toggleItem(item.id)}
                    disabled={busy}
                    data-testid={`item-toggle-${item.id}`}
                    className={`relative inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full transition-colors disabled:opacity-50 ${
                      available ? "bg-emerald-500" : "bg-zinc-700"
                    }`}
                    aria-label={available ? "Mark sold out" : "Mark available"}
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform mt-1 ${
                        available ? "translate-x-6" : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-10">
          {STATUS_FLOW.map((s) => (
            <div
              key={s.key}
              data-testid={`stat-${s.key}`}
              className="bg-[#111] border border-white/5 p-4"
            >
              <div
                className="font-['Outfit'] text-[10px] uppercase tracking-[0.3em]"
                style={{ color: s.color }}
              >
                {s.label}
              </div>
              <div className="font-['Anton'] text-3xl text-white mt-1">
                {stats.by_status?.[s.key] || 0}
              </div>
            </div>
          ))}
          <div
            data-testid="stat-archived"
            className="bg-[#111] border border-white/5 p-4"
          >
            <div className="font-['Outfit'] text-[10px] uppercase tracking-[0.3em] text-zinc-500">
              Archived
            </div>
            <div className="font-['Anton'] text-3xl text-white mt-1">
              {stats.archived || 0}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1 mb-8 border-b border-white/10">
          {TAB_OPTIONS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`tab-${t.key}`}
              className={`relative px-5 py-3 font-['Anton'] text-sm tracking-widest uppercase transition-colors ${
                tab === t.key
                  ? "text-white"
                  : "text-zinc-500 hover:text-zinc-300"
              }`}
            >
              {t.label}
              {tab === t.key && (
                <span className="absolute bottom-[-1px] left-0 right-0 h-[2px] bg-[#FF3B30]" />
              )}
            </button>
          ))}
        </div>

        {loading && orders.length === 0 ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-6 h-6 text-zinc-500 animate-spin" />
          </div>
        ) : orders.length === 0 ? (
          <div
            data-testid="admin-empty"
            className="border border-dashed border-white/10 py-20 text-center"
          >
            <p className="font-['Anton'] text-2xl uppercase tracking-widest text-zinc-600">
              No orders here
            </p>
            <p className="font-['Outfit'] text-sm text-zinc-600 mt-2">
              {tab === "active"
                ? "When customers order, cards will show up live."
                : "Orders you complete and archive will live here."}
            </p>
          </div>
        ) : tab === "active" && grouped ? (
          <div className="space-y-12">
            {STATUS_FLOW.map((s) => {
              const list = grouped[s.key] || [];
              if (list.length === 0) return null;
              return (
                <section key={s.key} data-testid={`section-${s.key}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <span
                      className="h-[2px] w-8"
                      style={{ background: s.color }}
                    />
                    <h2
                      className="font-['Anton'] text-xl uppercase tracking-[0.3em]"
                      style={{ color: s.color }}
                    >
                      {s.label} · {list.length}
                    </h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {list.map((o) => (
                      <OrderCard key={o.order_id} order={o} onChanged={refresh} />
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {orders.map((o) => (
              <OrderCard key={o.order_id} order={o} onChanged={refresh} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
